import { useEffect, useState } from "react";
import { Network, NetworkProgram } from "../../API";
import { QueryStatus } from "../../app/types";
import {
  createNetworkProgram,
  deleteNetworkProgram,
  fetchAllNetworks,
  fetchNetworkProgramsByPrograms
} from "./networksAPI";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { store } from "../../app/store";
import {
  selectNetworks,
  selectNetworksError,
  selectNetworksStatus,
  setNetworks,
  setNetworksError,
  setNetworksStatus
} from "./networksSlice";
import {
  selectNetworkProgramsByProgramIds,
  selectNetworkProgramsStatus,
  selectNetworkProgramsError,
  setNetworkPrograms,
  setNetworkProgramsStatus,
  setNetworkProgramsError
} from "./networkProgramsSlice";

export const useAllNetworks = () => {
  const allNetworks = useAppSelector(selectNetworks);
  const status = useAppSelector(selectNetworksStatus);
  const error = useAppSelector(selectNetworksError);

  const dispatch = useAppDispatch();

  const getAllNetworks = async () => {
    // If already loaded or currently loading, don't reload
    if (status === "loading" || status === "succeeded") {
      return;
    }

    dispatch(setNetworksStatus("loading"));
    dispatch(setNetworksError(undefined));

    try {
      const results = await fetchAllNetworks();
      dispatch(setNetworks(results));
      dispatch(setNetworksStatus("succeeded"));
    } catch (error) {
      console.error("error fetching all networks:", error);
      dispatch(setNetworksStatus("failed"));
      dispatch(setNetworksError(`Error loading networks: ${error}`));
    }
  };

  // No auto-load on mount - networks are loaded in auth flow for SysAdmins
  // Components that need networks should rely on Redux state or call getAllNetworks() explicitly

  return {
    networks: allNetworks,
    status,
    error,
    getAllNetworks
  };
};

// Module-level promise cache to prevent duplicate in-flight requests
// This is temporary until the request completes and Redux state is updated
const inFlightPromises = new Map<string, Promise<NetworkProgram[]>>();

export const useNetworkProgramsByPrograms = () => {
  const dispatch = useAppDispatch();
  const [lastRequestedProgramIds, setLastRequestedProgramIds] = useState<string[]>([]);

  const getNetworkProgramsByPrograms = async (programIds: string[]) => {
    const sortedIds = [...programIds].sort().join(",");
    
    // Get current Redux state for these programIds
    const state = store.getState();
    const currentStatus = selectNetworkProgramsStatus(state, programIds);
    const currentNetworkPrograms = selectNetworkProgramsByProgramIds(state, programIds);

    // Check if already loaded in Redux
    if (currentStatus === "succeeded" && currentNetworkPrograms.length > 0) {
      setLastRequestedProgramIds(programIds);
      return;
    }

    // Check if already loading (in-flight promise exists)
    const inFlightPromise = inFlightPromises.get(sortedIds);
    if (inFlightPromise) {
      setLastRequestedProgramIds(programIds);
      await inFlightPromise;
      return;
    }

    // Prevent duplicate calls with same programIds in this hook instance
    const lastSortedIds = [...lastRequestedProgramIds].sort().join(",");
    if (sortedIds === lastSortedIds && currentStatus === "loading") {
      return;
    }

    setLastRequestedProgramIds(programIds);
    dispatch(setNetworkProgramsStatus({ programIds, status: "loading" }));
    dispatch(setNetworkProgramsError({ programIds, error: undefined }));

    // Create promise and store it
    const apiPromise = fetchNetworkProgramsByPrograms(programIds).then(result => result || []);
    inFlightPromises.set(sortedIds, apiPromise);

    try {
      const results = await apiPromise;
      const finalResults = results || [];
      
      dispatch(setNetworkPrograms({ programIds, networkPrograms: finalResults }));
      dispatch(setNetworkProgramsStatus({ programIds, status: "succeeded" }));
    } catch (error) {
      console.error("error fetching networkprograms by program:", error);
      dispatch(setNetworkProgramsStatus({ programIds, status: "failed" }));
      dispatch(setNetworkProgramsError({ programIds, error: JSON.stringify(error) }));
    } finally {
      // Remove from in-flight cache once complete
      inFlightPromises.delete(sortedIds);
    }
  };

  // Get current programIds from last request, or use empty array
  const currentProgramIds = lastRequestedProgramIds.length > 0 
    ? lastRequestedProgramIds 
    : [];

  const networkPrograms = useAppSelector((state) =>
    selectNetworkProgramsByProgramIds(state, currentProgramIds)
  );
  const status = useAppSelector((state) =>
    selectNetworkProgramsStatus(state, currentProgramIds)
  );
  const error = useAppSelector((state) =>
    selectNetworkProgramsError(state, currentProgramIds)
  );

  return {
    networkPrograms,
    status,
    error,
    getNetworkProgramsByPrograms
  };
};

export const useNetworksByProgram = (programId?: string) => {
  const dispatch = useAppDispatch();
  const [networksAssociatedWithProgram, setNetworksAssociatedWithProgram] =
    useState<Network[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getNetworksByProgram = async (programId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const programIds = [programId];
      const sortedIds = programIds.sort().join(",");
      
      // Check Redux state first
      const state = store.getState();
      const currentStatus = selectNetworkProgramsStatus(state, programIds);
      const currentNetworkPrograms = selectNetworkProgramsByProgramIds(state, programIds);
      
      let results: NetworkProgram[] | null = null;
      
      if (currentStatus === "succeeded" && currentNetworkPrograms.length > 0) {
        // Already in Redux - use it
        results = currentNetworkPrograms;
      } else {
        // Check if in-flight
        const inFlightPromise = inFlightPromises.get(sortedIds);
        if (inFlightPromise) {
          results = await inFlightPromise;
        } else {
          // Make API call via Redux
          dispatch(setNetworkProgramsStatus({ programIds, status: "loading" }));
          dispatch(setNetworkProgramsError({ programIds, error: undefined }));
          
          const apiPromise = fetchNetworkProgramsByPrograms(programIds).then(result => result || []);
          inFlightPromises.set(sortedIds, apiPromise);
          
          try {
            results = await apiPromise;
            dispatch(setNetworkPrograms({ programIds, networkPrograms: results }));
            dispatch(setNetworkProgramsStatus({ programIds, status: "succeeded" }));
          } catch (err) {
            dispatch(setNetworkProgramsStatus({ programIds, status: "failed" }));
            dispatch(setNetworkProgramsError({ programIds, error: JSON.stringify(err) }));
            throw err;
          } finally {
            inFlightPromises.delete(sortedIds);
          }
        }
      }
      const networks: Network[] = [];
      if (results) {
        for (const networkProgram of results) {
          networks.push(networkProgram.network);
        }
      }
      setNetworksAssociatedWithProgram(networks);
      setStatus("succeeded");
      return networks;
    } catch (error) {
      const errorString = `${error}`;
      console.error("error fetching networks by program:", error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  useEffect(() => {
    if (programId) {
      getNetworksByProgram(programId);
    }
  }, [programId]);

  return {
    networksAssociatedWithProgram,
    status,
    error,
    getNetworksByProgram
  };
};

export const useUpdateProgramNetworks = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateProgramNetworks = async (
    programId: string,
    networkIds: string[]
  ) => {
    setStatus("loading");
    setError(null);
    try {
      const existingNetworkPrograms = await fetchNetworkProgramsByPrograms([
        programId
      ]);
      if (existingNetworkPrograms) {
        for (const existingNetworkProgram of existingNetworkPrograms) {
          await deleteNetworkProgram(existingNetworkProgram.id);
        }
      }
      if (networkIds) {
        for (const networkId of networkIds) {
          await createNetworkProgram(programId, networkId);
        }
      }
      setStatus("succeeded");
    } catch (error) {
      console.error("error updating program networks:", error);
      setStatus("failed");
      setError(`Error updating program networks: ${error}`);
    }
  };

  return {
    status,
    error,
    updateProgramNetworks
  };
};
